import * as React from 'react';
import styled from 'styled-components';

import {
    LocaleModel,
    RichtextFullElementModel,
    XfCampaignPriceModel
} from '../../../../generated/core';

import {AuthoringWrapper} from '../../../components/AuthoringWrapper';
import {
    useLocaleModel,
    useLogger,
    useModelStore,
    useRouterService
} from '../../../context';
import {MapTo} from '../../../infrastructure/compatibility/MapTo';

import {C} from '../../../registries/compatibilty';
import {isXfPreview} from '../../../utils/xfUtils';
import {RichtextFullElement} from '../../editorial/elements/RichtextElement';
import {ButtonElement} from '../../editorial/elements/buttonElement/ButtonElement';
import {
    CampaignPrice,
    CampaignPrices
} from '../../../d6/components/campaign-price';
import {LinkElement} from '../../editorial/elements/LinkElementComponent';
import {getMappedIcon} from '../../../utils/getMappedIcon';
import {BelowSectionDisclaimerContainer} from '../../../components/disclaimers/BelowSectionDisclaimerContainer';
import {GeneralDisclaimerProvider} from '../../../context/disclaimer/GeneralDisclaimerProvider';
import {IncentiveLoader} from '../../../components/victor/IncentiveLoader';
import {Incentive} from '@volkswagen-onehub-cms/cms-victor-bff';
import {convertVictorTenantType} from '../../../utils/convertVictorTenantType';
import {createDescriptionText} from './createDescriptionText';
import {Logger} from '../../../context/logger/Logger';
import {ModelStore} from '../../../context/model/ModelStore';

const StyledAuthorWrapper = styled.div<{isExperienceFragment: boolean}>`
    .mediaElement {
        width: 100%;
    }
`;

function createContentUpdater(
    validityTextPattern: string | undefined,
    locale: LocaleModel,
    logger: Logger,
    modelStore: ModelStore
) {
    return (path: string, incentive: Incentive) => {
        if (!validityTextPattern) {
            return;
        }
        const descriptionText = createDescriptionText(
            validityTextPattern,
            incentive,
            locale,
            logger
        );

        if (descriptionText) {
            modelStore.setMergedData<RichtextFullElementModel>(
                path + '/description',
                {
                    richtext: [{kind: 'textNode', value: descriptionText}]
                }
            );
        }
    };
}

const VictorEnabledXfCampaignPrice = (props: XfCampaignPriceProps) => {
    const {cqPath, incentiveId, victorTenantType, validityTextPattern} = props;
    const locale = useLocaleModel();
    const logger = useLogger();
    const modelStore = useModelStore();
    /* eslint-disable-next-line react-hooks/exhaustive-deps*/
    const contentUpdater = React.useCallback(
        createContentUpdater(validityTextPattern, locale, logger, modelStore),
        [validityTextPattern, locale, logger, modelStore]
    );
    return (
        <IncentiveLoader
            incentiveId={incentiveId}
            victorTenantType={convertVictorTenantType(victorTenantType)}
            path={cqPath}
            contentUpdater={contentUpdater}
        >
            <InternalXfCampaignPrice {...props} />
        </IncentiveLoader>
    );
};

interface XfCampaignPriceProps extends XfCampaignPriceModel {}

const InternalXfCampaignPrice: React.FunctionComponent<
    XfCampaignPriceProps
> = props => {
    const {
        leasingPrice,
        newPrice,
        oldPrice,
        discountPrice,
        oldPriceLabel,
        newPriceLabel,
        leasingPriceLabel
    } = props;

    const routerService = useRouterService();
    const xfPreview = isXfPreview(routerService.pagePath);

    const description = (
        <RichtextFullElement path="description" hideItemDisclaimers noElement />
    );
    const primaryButton = (
        <ButtonElement
            path="primaryButton"
            overrideIcon={getMappedIcon('Discount', 'default')}
            icon="Discount"
            noElement
        />
    );
    const buttonLink = <LinkElement path="buttonLink" noElement />;

    const prices = (
        <CampaignPrices
            oldPrice={oldPrice}
            newPrice={newPrice}
            leasingPrice={leasingPrice}
            discountPrice={discountPrice}
            newPriceLabel={newPriceLabel}
            oldPriceLabel={oldPriceLabel}
            leasingPriceLabel={leasingPriceLabel}
        />
    );

    const banner = (
        <GeneralDisclaimerProvider displayTypes={['T3_SECTION_BASED']}>
            <CampaignPrice
                description={description}
                buttonLink={buttonLink}
                primaryButton={primaryButton}
                prices={prices}
            />
            <BelowSectionDisclaimerContainer
                displayTypes={['T3_SECTION_BASED']}
            />
        </GeneralDisclaimerProvider>
    );

    const authorView = (
        <AuthoringWrapper title="Campaign Price XF">
            <StyledAuthorWrapper isExperienceFragment={xfPreview}>
                {banner}
            </StyledAuthorWrapper>
        </AuthoringWrapper>
    );

    const publishView = banner;

    return C.isInEditor() ? authorView : publishView;
};

interface XfCampaignPriceExtraProps {}

export const XfCampaignPrice = MapTo<XfCampaignPriceExtraProps>(
    'vwa-ngw18/components/structure/xfCampaignPrice',
    VictorEnabledXfCampaignPrice
);
